// Rack usage tracking: which input/output channels of each rack are already
// taken by musician/crew tracks, monitor outputs and shared sidefills. Used by
// the instrument editors to grey out occupied channels so nothing is patched
// twice on the same rack — and to show WHO occupies a channel on hover.

import type {
  StageRack,
  Instrument,
  SidefillConfig,
} from '../types';

/** Which channels of a rack are occupied, with the occupant's label
 * (e.g. "Ales — Bass", "Vorph — IEM", "Sidefill L"). Free = absent. */
export interface RackUsage {
  inputs: Record<string, Record<number, string>>;   // rackId -> { ch: occupant }
  outputs: Record<string, Record<number, string>>;  // rackId -> { ch: occupant }
  inputList: Record<number, string>;                // console input-list channel -> occupant
  /** 2026-10-23 (réciprocité du tick Shared) : person label → input-list
   *  channels declared sharedWithReplacement — le tick sur la piste du TITULAIRE
   *  ou du REMPLAÇANT libère le canal pour l'autre (la règle lit les deux sens). */
  sharedInputs?: Record<string, Set<number>>;
  /** idem pour les canaux RACK : person → rackId → Set<channel>. */
  sharedRack?: Record<string, Record<string, Set<number>>>;
}

/** How many input/output channels a rack exposes (defaults when unset). */
export function rackCapacities(rack: StageRack | undefined): { inputs: number; outputs: number } {
  return {
    inputs: rack?.inputs ?? 16,
    outputs: rack?.outputs ?? 8,
  };
}

function mark(
  usage: Record<string, Record<number, string>>,
  rackId: string | undefined,
  start: number,
  count: number,
  label: string,
) {
  if (!rackId || !start || start <= 0) return;
  const map = usage[rackId] ?? (usage[rackId] = {});
  for (let ch = start; ch < start + count; ch++) {
    if (ch >= 1 && !map[ch]) map[ch] = label; // first occupant wins
  }
}

/** Add an instrument track's input usage (tracks with direction 'input').
 * Also records the track's console input-list channel (inputListChannel) so
 * the input-list picker can grey out channels already assigned elsewhere.
 * Tracks carrying `sharedWithReplacement` are recorded in `sharedInputs` /
 * `sharedRack` maps (person label → channels) so the picker's replacement
 * rule works BOTH ways: a tick on the MAIN player's track (e.g. Zack's
 * Trumpet) frees the channel for the replacement's picker too — the tick
 * describes the PAIR, not one card. (2026-10-23, user-reported: ticks placed
 * on the main players had no effect on the replacement's greyed channels.) */
function collectInstrumentInputs(
  usage: Record<string, Record<number, string>>,
  inputList: Record<number, string>,
  instruments: Instrument[] | undefined,
  personLabel: string,
  sharedInputs?: Record<string, Set<number>>,
  sharedRack?: Record<string, Record<string, Set<number>>>,
) {
  for (const inst of instruments ?? []) {
    for (const track of inst.tracks ?? []) {
      if ((track.direction || 'input') === 'output') continue; // outputs → outputs map
      const label = `${personLabel} — ${track.name || inst.name || 'track'}`;
      mark(usage, track.rackId, track.ioChannel || 0, 1, label);
      const il = track.inputListChannel || 0;
      if (il >= 1 && !inputList[il]) inputList[il] = label; // first occupant wins
      if (track.sharedWithReplacement) {
        if (il >= 1) (sharedInputs![personLabel] ??= new Set<number>()).add(il);
        if (track.rackId && (track.ioChannel || 0) >= 1) {
          const byRack = sharedRack![personLabel] ?? (sharedRack![personLabel] = {});
          (byRack[track.rackId] ??= new Set<number>()).add(track.ioChannel);
        }
      }
    }
  }
}

/** Add an instrument track's OUTPUT usage (direction === 'output'). */
function collectInstrumentOutputs(
  usage: Record<string, Record<number, string>>,
  instruments: Instrument[] | undefined,
  personLabel: string,
) {
  for (const inst of instruments ?? []) {
    for (const track of inst.tracks ?? []) {
      if ((track.direction || 'input') !== 'output') continue;
      mark(usage, track.rackId, track.ioChannel || 0, 1, `${personLabel} — ${track.name || inst.name || 'track'}`);
    }
  }
}

/** Add a person's monitor output usage (IEM / wedges / subwoofer). */
function collectMonitorOutputs(
  usage: Record<string, Record<number, string>>,
  person: {
    iemType?: string;
    iemRackId?: string;
    iemOutStart?: number;
    wedges?: number;
    wedgeRackId?: string;
    wedgeOutStart?: number;
    wedgesStereo?: boolean;
    subwoofer?: boolean;
    subRackId?: string;
    subOutStart?: number;
  },
  personLabel: string,
) {
  // IEM: 1 channel mono, 2 channels stereo (consecutive from iemOutStart)
  if (person.iemType && person.iemType !== 'none') {
    const count = person.iemType === 'stereo' ? 2 : 1;
    mark(usage, person.iemRackId, person.iemOutStart || 0, count, `${personLabel} — IEM`);
  }
  // Wedges: stereo L/R = 2 channels (a pair), otherwise a single linked mono
  // feed distributed to all wedges = 1 channel.
  const wedges = person.wedges ?? 0;
  if (wedges > 0) {
    const stereo = wedges > 1 && person.wedgesStereo;
    const count = stereo ? 2 : 1;
    mark(usage, person.wedgeRackId, person.wedgeOutStart || 0, count, `${personLabel} — wedges`);
  }
  // Subwoofer: 1 channel
  if (person.subwoofer) {
    mark(usage, person.subRackId, person.subOutStart || 0, 1, `${personLabel} — sub`);
  }
}

/**
 * Compute the full rack usage for a band: all musicians + all crew tracks
 * (inputs) and all monitor outputs + shared sidefills (outputs).
 */
export function computeRackUsage(params: {
  musicians: { name?: string; instruments?: Instrument[]; iemType?: string; iemRackId?: string; iemOutStart?: number; wedges?: number; wedgeRackId?: string; wedgeOutStart?: number; wedgesStereo?: boolean; subwoofer?: boolean; subRackId?: string; subOutStart?: number }[];
  crew?: { name?: string; instruments?: Instrument[]; iemType?: string; iemRackId?: string; iemOutStart?: number; wedges?: number; wedgeRackId?: string; wedgeOutStart?: number; wedgesStereo?: boolean; subwoofer?: boolean; subRackId?: string; subOutStart?: number }[];
  sidefills?: SidefillConfig[];
  /** Sortie du mix principal vers la sono (paire stéréo = 2 canaux contigus). */
  paMix?: { rackId?: string; ch?: number };
}): RackUsage & { sharedInputs?: Record<string, Set<number>>; sharedRack?: Record<string, Record<string, Set<number>>> } {
  const inputs: Record<string, Record<number, string>> = {};
  const outputs: Record<string, Record<number, string>> = {};
  const inputList: Record<number, string> = {};
  // Shared-channel registry (2026-10-23): tracks ticked `sharedWithReplacement`
  // — person label → input-list channels / rack channels — so the pickers can
  // apply the replacement rule whichever card carries the tick.
  const sharedInputs: Record<string, Set<number>> = {};
  const sharedRack: Record<string, Record<string, Set<number>>> = {};

  for (const m of params.musicians ?? []) {
    const label = m.name || 'Unnamed';
    collectInstrumentInputs(inputs, inputList, m.instruments, label, sharedInputs, sharedRack);
    collectInstrumentOutputs(outputs, m.instruments, label);
    collectMonitorOutputs(outputs, m, label);
  }
  for (const c of params.crew ?? []) {
    const label = c.name || 'Unnamed';
    collectInstrumentInputs(inputs, inputList, c.instruments, label);
    collectInstrumentOutputs(outputs, c.instruments, label);
    collectMonitorOutputs(outputs, c, label);
  }
  for (const sf of params.sidefills ?? []) {
    if (sf && sf.channel) mark(outputs, sf.rackId, sf.channel, 1, sf.name || 'Sidefill');
  }
  // Mix principal vers la sono : paire stéréo L/R (2 canaux contigus).
  if (params.paMix && params.paMix.rackId && params.paMix.ch) {
    mark(outputs, params.paMix.rackId, params.paMix.ch, 2, 'PA mix');
  }
  return { inputs, outputs, inputList, sharedInputs, sharedRack };
}

/**
 * Return the occupant label of a channel on a rack when it is already used by
 * someone ELSE (excluding `selfRackId`/`selfChannel`, which is the field being
 * edited). Returns `undefined` when the channel is free.
 */
export function isChannelTaken(
  usage: RackUsage | undefined,
  kind: 'inputs' | 'outputs',
  rackId: string,
  channel: number,
  selfRackId?: string,
  selfChannel?: number,
): string | undefined {
  if (!usage || !rackId || !channel) return undefined;
  const occupant = usage[kind][rackId]?.[channel];
  if (!occupant) return undefined;
  // The value being edited right now occupies this channel — do not grey it.
  if (selfRackId === rackId && selfChannel === channel) return undefined;
  return occupant;
}

/**
 * Does an occupant (identified by their label, e.g. "Ales — IEM") share at
 * least one instrument NAME with the person currently being edited? Used for
 * the replacement/guest rule on MONITOR OUTPUTS: a guest who plays the same
 * instrument as the main player reuses the main's IEM/wedges/sub channels
 * (they never play at the same time), so those channels are treated as free.
 *
 * `personInstrumentsMap` maps every OTHER person's label → the set of
 * instrument names they play (built by buildPersonInstruments). `editedInstruments`
 * is the set of instrument names of the person being edited. Returns true when
 * the occupant's label matches a person whose instrument set intersects the
 * edited person's instrument set.
 */
export function occupantSharesAnyInstrument(
  occupantLabel: string,
  personInstrumentsMap: Record<string, Set<string>> | undefined,
  editedInstruments: Set<string> | undefined,
): boolean {
  if (!occupantLabel || !personInstrumentsMap || !editedInstruments || editedInstruments.size === 0) {
    return false;
  }
  for (const [label, insts] of Object.entries(personInstrumentsMap)) {
    if (occupantLabel.startsWith(label)) {
      for (const name of insts) {
        if (editedInstruments.has(name)) return true;
      }
      return false;
    }
  }
  return false;
}

/**
 * Does an occupant (identified by their label) share at least one TAG with the
 * person currently being edited? Used for the replacement/guest rule on CREW
 * members: a guest/session crew member who shares a tag (e.g. 'mon', 'foh',
 * 'lights') with the main crew member reuses the main's monitor/input channels
 * (they never work at the same time), so those channels are treated as free.
 *
 * `personTagsMap` maps every OTHER person's label → the set of tags they carry
 * (built by buildPersonTags). `editedTags` is the set of tags of the person
 * being edited. Returns true when the occupant's label matches a person whose
 * tag set intersects the edited person's tag set.
 */
export function occupantSharesAnyTag(
  occupantLabel: string,
  personTagsMap: Record<string, Set<string>> | undefined,
  editedTags: Set<string> | undefined,
): boolean {
  if (!occupantLabel || !personTagsMap || !editedTags || editedTags.size === 0) {
    return false;
  }
  for (const [label, tags] of Object.entries(personTagsMap)) {
    if (occupantLabel.startsWith(label)) {
      for (const tag of tags) {
        if (editedTags.has(tag)) return true;
      }
      return false;
    }
  }
  return false;
}

/**
 * Build the `personTags` map (person label → set of tags) from the band's full
 * musician + crew lists. Both Musician and CrewMember carry `tags?: string[]`.
 */
export function buildPersonTags(
  musicians: { name?: string; tags?: string[] }[],
  crew: { name?: string; tags?: string[] }[],
): Record<string, Set<string>> {
  const map: Record<string, Set<string>> = {};
  const add = (person: { name?: string; tags?: string[] }) => {
    const label = person.name || 'Unnamed';
    const set = map[label] ?? (map[label] = new Set<string>());
    for (const tag of person.tags ?? []) {
      if (tag) set.add(tag);
    }
  };
  for (const m of musicians ?? []) add(m);
  for (const c of crew ?? []) add(c);
  return map;
}

/**
 * Return the occupant label of a channel on a rack when it is already used by
 * someone ELSE (excluding `selfRackId`/`selfChannel`, the field being edited),
 * AND that occupant does NOT share an instrument with the person being edited
 * (replacement/guest rule for monitor outputs). Returns `undefined` when the
 * channel is free or shared.
 */
export function isChannelTakenForOutput(
  usage: RackUsage | undefined,
  kind: 'inputs' | 'outputs',
  rackId: string,
  channel: number,
  selfRackId?: string,
  selfChannel?: number,
  editedInstruments?: Set<string>,
  personInstrumentsMap?: Record<string, Set<string>>,
  editedTags?: Set<string>,
  personTagsMap?: Record<string, Set<string>>,
  /** Label of the person being edited (e.g. "Guillaume"). Channels occupied
   *  by that person's OWN other tracks are ALWAYS taken — they used to be
   *  reported as free because the guest rule matched the person with
   *  themselves. (2026-09-20 fix) */
  selfLabel?: string,
  /** TRUE only when the edited track DECLARES that its channel is shared with
   *  a replacement/guest (`Track.sharedWithReplacement`). Default FALSE =
   *  strict occupancy: any channel already patched by someone else is taken,
   *  whatever instrument they play. (2026-09-20, option C) */
  allowShared?: boolean,
): string | undefined {
  const occupant = isChannelTaken(usage, kind, rackId, channel, selfRackId, selfChannel);
  if (!occupant) return undefined;
  // Someone's own channel is never "shared with a replacement" — it is busy.
  if (selfLabel && occupant.startsWith(`${selfLabel} —`)) return occupant;
  // BIDIRECTIONAL tick (2026-10-23, option A): the occupant's own track may
  // declare the sharing — that frees the channel even when the edited track
  // does not tick it.
  if (!allowShared) {
    const owner = Object.keys(usage?.sharedRack || {}).find((p) => occupant.startsWith(`${p} —`));
    const sameInstrument = editedInstruments && editedInstruments.size > 0
      ? occupantSharesAnyInstrument(occupant, personInstrumentsMap, editedInstruments)
      : true;
    if (owner && usage?.sharedRack?.[owner]?.[rackId]?.has(channel) && sameInstrument) return undefined;
    return occupant;
  }
  if (occupantSharesAnyInstrument(occupant, personInstrumentsMap, editedInstruments)) return undefined;
  if (occupantSharesAnyTag(occupant, personTagsMap, editedTags)) return undefined;
  return occupant;
}

/**
 * Return the occupant label of a console input-list channel when it is already
 * used by someone ELSE (excluding `selfChannel`, the field being edited).
 * Returns `undefined` when the channel is free.
 */
export function isInputListTaken(
  usage: RackUsage | undefined,
  channel: number,
  selfChannel?: number,
  editedInstruments?: Set<string>,
  personInstrumentsMap?: Record<string, Set<string>>,
  editedTags?: Set<string>,
  personTagsMap?: Record<string, Set<string>>,
  /** Label of the person being edited — their own channels are always taken. */
  selfLabel?: string,
  /** TRUE only when the edited track declares a shared channel with a
   *  replacement/guest. Default FALSE = strict. (2026-09-20, option C) */
  allowShared?: boolean,
): string | undefined {
  if (!usage || !channel) return undefined;
  const occupant = usage.inputList[channel];
  if (!occupant) return undefined;
  if (selfChannel === channel) return undefined;
  if (selfLabel && occupant.startsWith(`${selfLabel} —`)) return occupant;
  // Replacement/guest rule — BIDIRECTIONAL (2026-10-23, option A user): the tick
  // describes the PAIR. It is honoured when EITHER the edited track declares
  // sharing (`allowShared`) OR the OCCUPANT's own track does (the tick placed
  // on the main player's card frees the channel for the replacement's picker).
  if (!allowShared) {
    // The occupant-side direction: the tick placed on the MAIN player's card
    // frees the channel for the replacement's picker — but ONLY for a track
    // of the SAME instrument (Tim the trumpet replacement must not inherit
    // Ross's trombone channels): the occupant's shared declaration applies to
    // occupant tracks of the instrument the EDITED person plays.
    const owner = occupant.startsWith(`${selfLabel || '\u0000'} —`)
      ? undefined
      : Object.keys(usage.sharedInputs || {}).find((p) => occupant.startsWith(`${p} —`));
    if (owner) {
      const chs = usage.sharedInputs?.[owner];
      // Same instrument required — but the edited person's instrument set may
      // be UNAVAILABLE at this call site (undefined map). In that case fall
      // back to the NAME match on the occupant label vs the edited person's
      // instruments via `editedInstruments` only when it is non-empty; when
      // neither is available, the tick alone decides (permissive, as option A
      // was described: "une piste marquée shared chez Zack OU chez Tim libère
      // le canal pour l'autre").
      const sameInstrument = editedInstruments && editedInstruments.size > 0
        ? occupantSharesAnyInstrument(occupant, personInstrumentsMap, editedInstruments)
        : true;
      if (chs?.has(channel) && sameInstrument) return undefined;
    }
    return occupant;
  }
  // Replacement/guest rule: a channel occupied by someone who plays the SAME
  // instrument (or carries the SAME tag, for crew) as the person being edited
  // is treated as free (they never play/work at the same time).
  if (occupantSharesAnyInstrument(occupant, personInstrumentsMap, editedInstruments)) return undefined;
  if (occupantSharesAnyTag(occupant, personTagsMap, editedTags)) return undefined;
  return occupant;
}
